<script lang="ts" setup>
import { ref, computed, useTemplateRef, onMounted } from 'vue'
import { useForm, useField } from 'vee-validate'
import { OSwitch } from '@oruga-ui/oruga-next'

import FormMessages from '@tech77/hydra/components/FormMessages.vue'
import FormLegalNotice from '@tech77/hydra/components/FormLegalNotice.vue'
import FieldText from '@tech77/hydra/components/FieldText.vue'
import FieldTextarea from '@tech77/hydra/components/FieldTextarea.vue'
import FieldPhone from '@tech77/hydra/components/FieldPhone.vue'
import FieldHidden from '@tech77/hydra/components/FieldHidden.vue'
import FieldEmail from '@tech77/hydra/components/FieldEmail.vue'

import { createSchema } from './schema'

enum Status {
  pristine = 'PRISTINE',
  dirty = 'DIRTY',
  error = 'ERROR',
  success = 'SUCCESS'
}

const props = withDefaults(
  defineProps<{
    modal?: boolean
    formTitle?: string
    successMessage?: string
    errorMessage?: string
    page?: string
    title?: string
  }>(), {
    modal: true,
    formTitle: 'Напишите нам',
    successMessage: 'Спасибо, Ваш запрос отправлен!',
    errorMessage: 'Ошибка при обработке запроса.',
    page: 'No page',
    title: 'No title'
  }
)

const emit = defineEmits<{
  (e: 'close'): void // oruga event
}>()

const { value } = useField<boolean>(() => 'agreement', undefined, {})

const status = ref<Status>(Status.pristine)

const lock = ref(false)

const form = useTemplateRef<HTMLFormElement | null>('form')

const isNotSuccess = computed(() => {
  return status.value !== Status.success
})

const initialValues = {
  WEB_FORM_ID: 6,
  form_text_50: '',
  form_text_52: '',
  form_email_51: '',
  form_textarea_53: '',
  form_hidden_55: '',
  agreement: false

}

const validationSchema = createSchema()

const { handleSubmit, resetForm, meta } = useForm({
  /**
   * Начальные значения полей формы.
   */
  initialValues,
  /**
   * Yup схема проверки полей.
   */
  validationSchema,
  /**
   * Запустить проверку полей
   * при монтировании компонента.
   */
  // validateOnMount: true,
  /**
   * Сохранять значения полей
   * в контексте даже после отключения
   * компонента.
   */
  // keepValuesOnUnmount: true,
  /**
   * Отметить поле при инициализации
   * как `touched`. Используется чтобы
   * явно указать состояние поля, без
   * ожидания действий пользователя.
   */
  initialTouched: {
    WEB_FORM_ID: true,
    form_text_50: false,
    form_text_52: false,
    form_email_51: false,
    form_textarea_53: false,
    form_hidden_55: false,
    agreement: false
  }
})

function closeSuccessNotification() {
  status.value = Status.pristine;
  lock.value = false;
  resetForm();

  if (props.modal) {
    emit("close");
  }
}

function closeErrorNotification() {
  status.value = Status.dirty
  lock.value = false
}

onMounted(() => {
  // console.log('Comments form mounted')
})

const onSubmit = handleSubmit(async (/* values */) => {
  if (!form.value) {
    console.warn("Form element is not available.");
    return;
  }

  if (!window.BX) {
    console.warn("Требуется window.BX");
    return;
  }

  lock.value = true;

  const formData = new FormData(form.value);

  try {
    await window.BX.ajax.runAction('platform:main.api.form.submit', {
      data: formData
    })
    status.value = Status.success
  } catch (e) {
    console.log('form error', e)
    status.value = Status.error
  } finally {
    lock.value = false
  }
})
</script>

<template>
  <div
    class="component-comments-form"
    :class="{
      'form-in-modal': modal,
    }"
  >
    <div class="form-container form-container--comments content">
      <div
        v-if="isNotSuccess"
        class="form-header mb-4"
      >
      </div>

      <FormMessages
        :status="status"
        :show-home-link="false"
        @close:error="closeErrorNotification"
        @close:success="closeSuccessNotification"
      />

      <div
        v-if="isNotSuccess"
        class="form-wrapper"
      >
        <form
          ref="form"
          novalidate
          @submit="onSubmit"
        >
          <FieldHidden name="WEB_FORM_ID" />
          <input type="hidden" class="input" name="form_hidden_39" value="Да">

          <div class="columns is-multiline">
            <div class="column is-12-tablet is-6-desktop">
              <FieldText
                name="form_text_50"
                label="Ваши имя и фамилия"
                help="Укажите ваши контактные данные, имя, фамилию."
                :disabled="lock"
                :asterisk="false"
              />

              <FieldPhone
                name="form_text_52"
                :disabled="lock"
                :asterisk="false"
              />

              <FieldEmail
                name="form_email_51"
                :disabled="lock"
                :asterisk="false"
              />
            </div>

            <div class="column is-12-tablet is-6-desktop">
              <FieldTextarea
                name="form_textarea_53"
                label="Текст обращения"
                placeholder="Текст обращения"
                :disabled="lock"
                :asterisk="false"
              />
            </div>
          </div>

          <div class="field ">
            <div class="control">
              <label class="checkbox">
                <input type="checkbox" name="form_checkbox_com_copy[]" value="54" wfd-id="id5">
                я хочу получить ответ на эл. почту
              </label>
            </div>
          </div>

          <div class="form-privacy">
            <FormLegalNotice
              privacy-link="/assets/files/legal-information/Policy-personal.pdf"
              agreement-link="/agreement/personal-information/"
              terms-link="/agreement/"
              hide-personal
            />

            <div class="switch-pr">
              <o-switch
                v-model="value"
                name="agreement"
                label="Согласен с условиями *"
              />
            </div>
          </div>

          <div class="buttons pt-5">
            <button
              class="button is-danger has-text-white radius-5"
              type="submit"
              :disabled="!meta.valid || !value"
            >
              Отправить
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<style>
.form-container--comments .form-privacy a {
  color: #2e82ff;
}

.form-container--comments .o-switch__input:checked {
  background-color: #fe3294;
}
.form-container--comments .select select,
.form-container--comments .input,
.form-container--comments .textarea {
  color: #000;
}
</style>
